import { TEA_PRODUCT_NAMES } from '@/constants/products'
import { getAboutContent, getLearnContent, getSeoContent } from '@/content/siteContent'
import type { AuthResponse } from '@/types/auth'
import type { AboutPageContent, LearnPageContent } from '@/types/content'
import type { UploadedImage } from './uploads.api'
import type {
  ECPayCallbackData,
  PayPalCaptureResponse,
  PayPalOrderResponse,
  PaymentRequest,
  PaymentResponse,
  PaymentVerification,
} from './payments.api'
import type { CreateOrderRequest, Order, OrderListResponse } from './orders.api'
import { createInitialMockState, type MockProductRecord } from './mockData'
import { ApiRequestError, type ApiTransport, type ApiTransportRequest } from './transport'

type ProductDetailResponse = {
  success: boolean
  data?: MockProductRecord
  message?: string
}

const state = createInitialMockState()

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const clone = <T>(value: T): T => {
  if (value === undefined || value === null) {
    return value
  }

  if (typeof structuredClone === 'function') {
    return structuredClone(value)
  }

  return JSON.parse(JSON.stringify(value)) as T
}

const toNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const paginate = <T>(items: T[], page: number, pageSize: number) => {
  const start = (page - 1) * pageSize
  const pageItems = items.slice(start, start + pageSize)
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize))

  return {
    items: pageItems,
    totalCount: items.length,
    page,
    pageSize,
    totalPages,
    hasNextPage: page < totalPages,
  }
}

const getPage = (params?: Record<string, unknown>) => toNumber(params?.page, 1)

const getPageSize = (params?: Record<string, unknown>, fallback = 12) =>
  toNumber(params?.pageSize ?? params?.limit, fallback)

const getLanguage = (params?: Record<string, unknown>) => (params?.lang === 'zh' ? 'zh' : 'en')

const findProductById = (productId: string) => state.catalogProducts.find(product => product.id === productId)

const filterCatalogProducts = (params?: Record<string, unknown>) => {
  const searchKeyword = typeof params?.searchKeyword === 'string'
    ? params.searchKeyword.trim().toLowerCase()
    : typeof params?.search === 'string'
      ? params.search.trim().toLowerCase()
      : ''

  const category = typeof params?.category === 'string' ? params.category : undefined
  const format = typeof params?.format === 'string' ? params.format : undefined

  return state.catalogProducts.filter(product => {
    if (category && product.category !== category) {
      return false
    }

    if (format && product.format !== format) {
      return false
    }

    if (!searchKeyword) {
      return true
    }

    const names = TEA_PRODUCT_NAMES[product.key]
    return names.en.toLowerCase().includes(searchKeyword) || names.zh.toLowerCase().includes(searchKeyword)
  })
}

const updateProduct = (productId: string, updater: (current: MockProductRecord) => MockProductRecord) => {
  const index = state.catalogProducts.findIndex(product => product.id === productId)

  if (index < 0) {
    throw new ApiRequestError({
      message: 'Product not found',
      status: 404,
    })
  }

  state.catalogProducts[index] = updater(state.catalogProducts[index])
  return state.catalogProducts[index]
}

const createOrder = (payload: CreateOrderRequest): Order => {
  const now = new Date().toISOString()
  const order: Order = {
    id: `mock-order-${state.orders.length + 1}`,
    userId: state.authResponse.userId,
    items: payload.items,
    totalAmount: payload.items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    currency: payload.currency || 'TWD',
    shippingAddress: payload.shippingAddress,
    paymentStatus: 'pending',
    paymentMethod: payload.paymentMethod,
    orderStatus: 'pending',
    createdAt: now,
    updatedAt: now,
  }

  state.orders.unshift(order)
  return order
}

const listOrders = (params?: Record<string, unknown>): OrderListResponse => {
  const status = typeof params?.status === 'string' ? params.status : undefined
  const paymentStatus = typeof params?.paymentStatus === 'string' ? params.paymentStatus : undefined
  const filtered = state.orders.filter(order => {
    if (status && order.orderStatus !== status) {
      return false
    }

    if (paymentStatus && order.paymentStatus !== paymentStatus) {
      return false
    }

    return true
  })
  const page = getPage(params)
  const pageSize = getPageSize(params, 10)
  const paged = paginate(filtered, page, pageSize)

  return {
    data: paged.items,
    total: paged.totalCount,
    page: paged.page,
    limit: paged.pageSize,
  }
}

const requireOrder = (orderId: string) => {
  const order = state.orders.find(item => item.id === orderId)

  if (!order) {
    throw new ApiRequestError({
      message: 'Order not found',
      status: 404,
    })
  }

  return order
}

const updateOrder = (orderId: string, updater: (current: Order) => Order) => {
  const index = state.orders.findIndex(order => order.id === orderId)

  if (index < 0) {
    throw new ApiRequestError({
      message: 'Order not found',
      status: 404,
    })
  }

  state.orders[index] = updater(state.orders[index])
  return state.orders[index]
}

const createPayment = (request: PaymentRequest): PaymentResponse => {
  const transactionId = `mock-payment-${state.payments.length + 1}`
  const verification: PaymentVerification = {
    transactionId,
    merchantTradeNo: transactionId,
    orderId: request.orderId,
    amount: request.amount,
    currency: request.currency,
    status: 'pending',
    provider: request.provider,
  }

  state.payments.unshift(verification)

  return {
    transactionId,
    merchantTradeNo: transactionId,
    status: 'pending',
    paymentUrl: request.provider === 'paypal'
      ? `https://www.sandbox.paypal.com/checkoutnow?token=${transactionId}`
      : request.provider === 'neweb_pay'
        ? `https://ccore.newebpay.com/MPG/mpg_gateway?mock=${transactionId}`
        : `https://payment-stage.ecpay.com.tw/mock/${transactionId}`,
    formData: request.provider === 'neweb_pay'
      ? {
          MerchantID: 'MOCK',
          TradeInfo: transactionId,
          TradeSha: 'mock',
          Version: '2.0',
        }
      : undefined,
    message: 'Mock payment created',
  }
}

const requirePayment = (transactionId: string) => {
  const payment = state.payments.find(item => item.transactionId === transactionId)

  if (!payment) {
    throw new ApiRequestError({
      message: 'Payment not found',
      status: 404,
    })
  }

  return payment
}

const updatePayment = (transactionId: string, updater: (current: PaymentVerification) => PaymentVerification) => {
  const index = state.payments.findIndex(payment => payment.transactionId === transactionId)

  if (index < 0) {
    throw new ApiRequestError({
      message: 'Payment not found',
      status: 404,
    })
  }

  state.payments[index] = updater(state.payments[index])
  return state.payments[index]
}

const createPayPalOrder = (orderId: string): PayPalOrderResponse => ({
  id: `mock-paypal-order-${orderId}`,
  status: 'CREATED',
  links: [
    {
      href: `https://www.sandbox.paypal.com/checkoutnow?token=mock-paypal-order-${orderId}`,
      rel: 'approve',
      method: 'GET',
    },
  ],
})

const capturePayPalOrder = (paypalOrderId: string): PayPalCaptureResponse => ({
  id: paypalOrderId,
  status: 'COMPLETED',
  purchase_units: [
    {
      payments: {
        captures: [
          {
            id: `capture-${paypalOrderId}`,
            status: 'COMPLETED',
            amount: {
              currency_code: 'USD',
              value: '0.00',
            },
          },
        ],
      },
    },
  ],
})

const createUploadedImage = (formData: FormData): UploadedImage => {
  const file = formData.get('file')
  const uploadId = `mock-upload-${state.uploads.length + 1}`
  const filename = file && typeof file === 'object' && 'name' in file && typeof file.name === 'string'
    ? file.name
    : `${uploadId}.png`
  const size = file && typeof file === 'object' && 'size' in file && typeof file.size === 'number'
    ? file.size
    : 0
  const mimeType = file && typeof file === 'object' && 'type' in file && typeof file.type === 'string'
    ? file.type
    : 'image/png'
  const url = file instanceof Blob && typeof URL.createObjectURL === 'function'
    ? URL.createObjectURL(file)
    : `/mock-uploads/${filename}`

  return {
    id: uploadId,
    url,
    filename,
    size,
    mimeType,
    uploadedAt: new Date().toISOString(),
  }
}

export class MockTransport implements ApiTransport {
  async request<T>({ method, endpoint, params, data }: ApiTransportRequest): Promise<T> {
    await wait(80)

    if (method === 'GET' && endpoint === '/api/content/about') {
      const lang = getLanguage(params)
      const aboutContent = getAboutContent(lang)
      const content = clone({
        ...aboutContent,
        seo: getSeoContent(lang).about,
      }) as AboutPageContent

      return content as T
    }

    if (method === 'GET' && endpoint === '/api/content/learn') {
      const lang = getLanguage(params)
      const learnContent = getLearnContent(lang)
      const content = clone({
        ...learnContent,
        seo: getSeoContent(lang).learn,
      }) as LearnPageContent

      return content as T
    }

    if (method === 'POST' && endpoint === '/api/GoogleAuth/Authenticate') {
      const payload = data as { IdToken?: string } | undefined
      const authResponse: AuthResponse = {
        ...state.authResponse,
        accessToken: payload?.IdToken ? `mock-${payload.IdToken.slice(0, 12)}` : state.authResponse.accessToken,
        lastLoginAt: new Date().toISOString(),
      }
      state.authResponse = authResponse
      return clone(authResponse) as T
    }

    if (method === 'GET' && endpoint === '/api/products') {
      const page = getPage(params)
      const pageSize = getPageSize(params, state.catalogProducts.length)
      const filteredProducts = filterCatalogProducts(params)
      return clone(paginate(filteredProducts, page, pageSize)) as T
    }

    if (method === 'GET' && endpoint === '/api/products/featured') {
      const page = getPage(params)
      const pageSize = getPageSize(params, state.featuredProducts.length)
      return clone(paginate(state.featuredProducts, page, pageSize)) as T
    }

    if (method === 'GET' && endpoint === '/api/products/showcase') {
      const page = getPage(params)
      const pageSize = getPageSize(params, state.showcaseProducts.length)
      return clone(paginate(state.showcaseProducts, page, pageSize)) as T
    }

    const productDetailMatch = endpoint.match(/^\/api\/products\/([^/]+)$/)
    if (method === 'GET' && productDetailMatch) {
      const product = findProductById(productDetailMatch[1])
      const response: ProductDetailResponse = product
        ? { success: true, data: product }
        : { success: false, message: 'Product not found' }
      return clone(response) as T
    }

    if (method === 'POST' && endpoint === '/api/admin/products') {
      const payload = (data ?? {}) as Partial<MockProductRecord>
      const id = typeof payload.id === 'string' && payload.id.trim() ? payload.id : `mock-product-${state.catalogProducts.length + 1}`
      const record: MockProductRecord = {
        id,
        key: payload.key ?? 'i',
        name: payload.name,
        price: payload.price ?? 0,
        category: payload.category,
        format: payload.format,
        stock: payload.stock,
        primaryImageId: payload.primaryImageId ?? `img-${id}`,
        primaryImageUrl: payload.primaryImageUrl,
        description: payload.description,
        images: payload.images ?? [],
        sku: payload.sku,
      }

      state.catalogProducts.unshift(record)
      return clone(record) as T
    }

    const productAdminMatch = endpoint.match(/^\/api\/admin\/products\/([^/]+)$/)
    if (productAdminMatch && method === 'PUT') {
      return clone(updateProduct(productAdminMatch[1], current => ({
        ...current,
        ...(data as Partial<MockProductRecord>),
      }))) as T
    }

    if (productAdminMatch && method === 'DELETE') {
      const index = state.catalogProducts.findIndex(product => product.id === productAdminMatch[1])
      if (index < 0) {
        throw new ApiRequestError({
          message: 'Product not found',
          status: 404,
        })
      }

      state.catalogProducts.splice(index, 1)
      return undefined as T
    }

    const productStockMatch = endpoint.match(/^\/api\/admin\/products\/([^/]+)\/stock$/)
    if (productStockMatch && method === 'PATCH') {
      const payload = (data ?? {}) as { stock?: number }
      return clone(updateProduct(productStockMatch[1], current => ({
        ...current,
        stock: payload.stock,
      }))) as T
    }

    if (method === 'POST' && endpoint === '/api/orders') {
      return clone(createOrder(data as CreateOrderRequest)) as T
    }

    if (method === 'GET' && endpoint === '/api/orders') {
      return clone(listOrders(params)) as T
    }

    const orderDetailMatch = endpoint.match(/^\/api\/orders\/([^/]+)$/)
    if (method === 'GET' && orderDetailMatch) {
      return clone(requireOrder(orderDetailMatch[1])) as T
    }

    const cancelOrderMatch = endpoint.match(/^\/api\/orders\/([^/]+)\/cancel$/)
    if (method === 'PATCH' && cancelOrderMatch) {
      return clone(updateOrder(cancelOrderMatch[1], current => ({
        ...current,
        orderStatus: 'cancelled',
        updatedAt: new Date().toISOString(),
      }))) as T
    }

    if (method === 'GET' && endpoint === '/api/admin/orders') {
      return clone(listOrders(params)) as T
    }

    const adminOrderStatusMatch = endpoint.match(/^\/api\/admin\/orders\/([^/]+)\/status$/)
    if (method === 'PATCH' && adminOrderStatusMatch) {
      const payload = (data ?? {}) as { status?: Order['orderStatus'] }
      return clone(updateOrder(adminOrderStatusMatch[1], current => ({
        ...current,
        orderStatus: payload.status ?? current.orderStatus,
        updatedAt: new Date().toISOString(),
      }))) as T
    }

    const adminOrderTrackingMatch = endpoint.match(/^\/api\/admin\/orders\/([^/]+)\/tracking$/)
    if (method === 'PATCH' && adminOrderTrackingMatch) {
      const payload = (data ?? {}) as { trackingNumber?: string }
      return clone(updateOrder(adminOrderTrackingMatch[1], current => ({
        ...current,
        trackingNumber: payload.trackingNumber,
        updatedAt: new Date().toISOString(),
      }))) as T
    }

    if (method === 'POST' && endpoint === '/api/payments/create') {
      return clone(createPayment(data as PaymentRequest)) as T
    }

    if (method === 'POST' && endpoint === '/api/payments/ecpay/verify') {
      const payload = data as ECPayCallbackData
      const transactionId = payload.MerchantTradeNo || payload.TradeNo || `mock-payment-${state.payments.length + 1}`
      const existing = state.payments.find(item => item.transactionId === transactionId)
      const payment = existing
        ? updatePayment(transactionId, current => ({
            ...current,
            status: 'paid',
            paidAt: payload.PaymentDate,
          }))
        : {
            transactionId,
            orderId: payload.MerchantTradeNo,
            amount: Number(payload.TradeAmt),
            currency: 'TWD',
            status: 'paid' as const,
            paidAt: payload.PaymentDate,
            provider: 'ecpay' as const,
          }

      if (!existing) {
        state.payments.unshift(payment)
      }

      return clone(payment) as T
    }

    if (method === 'POST' && endpoint === '/api/payments/paypal/create-order') {
      const payload = (data ?? {}) as { orderId: string; amount: number }
      return clone(createPayPalOrder(payload.orderId)) as T
    }

    if (method === 'POST' && endpoint === '/api/payments/paypal/capture') {
      const payload = (data ?? {}) as { orderID: string }
      return clone(capturePayPalOrder(payload.orderID)) as T
    }

    const paymentStatusMatch = endpoint.match(/^\/api\/payments\/([^/]+)$/)
    if (method === 'GET' && paymentStatusMatch) {
      return clone(requirePayment(paymentStatusMatch[1])) as T
    }

    const paymentRefundMatch = endpoint.match(/^\/api\/payments\/([^/]+)\/refund$/)
    if (method === 'POST' && paymentRefundMatch) {
      return clone(updatePayment(paymentRefundMatch[1], current => ({
        ...current,
        status: 'refunded',
      }))) as T
    }

    if (method === 'POST' && endpoint === '/api/uploads/image') {
      if (!(data instanceof FormData)) {
        throw new ApiRequestError({
          message: 'Upload payload must be FormData',
          status: 400,
        })
      }

      const uploadedImage = createUploadedImage(data)
      state.uploads.unshift(uploadedImage)
      return clone(uploadedImage) as T
    }

    const uploadDeleteMatch = endpoint.match(/^\/api\/uploads\/([^/]+)$/)
    if (method === 'DELETE' && uploadDeleteMatch) {
      const index = state.uploads.findIndex(upload => upload.id === uploadDeleteMatch[1])
      if (index < 0) {
        throw new ApiRequestError({
          message: 'Upload not found',
          status: 404,
        })
      }

      state.uploads.splice(index, 1)
      return undefined as T
    }

    if (method === 'GET' && endpoint === '/api/admin/uploads') {
      const page = getPage(params)
      const pageSize = getPageSize(params, 20)
      const paged = paginate(state.uploads, page, pageSize)

      return clone({
        data: paged.items,
        total: paged.totalCount,
      }) as T
    }

    throw new ApiRequestError({
      message: `Mock endpoint not implemented: ${method} ${endpoint}`,
      status: 501,
    })
  }
}
